import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

const LEAVE_TYPES = ['casual', 'sick', 'paid', 'unpaid'];
const ATTENDANCE_STATUSES = ['present', 'absent', 'weekly_off', 'holiday', 'leave'];

const WEEK_DAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

export default function PayrollPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);

  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [slips, setSlips] = useState([]);

  const [holidayForm, setHolidayForm] = useState({
    name: '',
    holiday_date: '',
    holiday_type: 'company',
    is_optional: false,
  });
  const [leaveForm, setLeaveForm] = useState({
    staff_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
  });
  const [attendanceForm, setAttendanceForm] = useState({
    staff_id: '',
    attendance_date: '',
    status: 'present',
    leave_type: 'casual',
    note: '',
  });

  const [adjustments, setAdjustments] = useState({});
  const [incentiveRuleDraft, setIncentiveRuleDraft] = useState({
    name: 'Default Incentive Rule',
    basis: 'collections',
    rate_percent: 2,
    min_threshold: 0,
    max_payout: 0,
    fixed_bonus: 0,
    effective_from: '',
  });
  const [activeIncentiveRule, setActiveIncentiveRule] = useState(null);
  const [draftIncentiveRule, setDraftIncentiveRule] = useState(null);
  const [incentiveSimulation, setIncentiveSimulation] = useState(null);
  const [incentiveLoading, setIncentiveLoading] = useState(false);

  const years = useMemo(() => {
    const base = now.getFullYear();
    return [base - 2, base - 1, base, base + 1];
  }, []);

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const [staffRes, holidaysRes, leavesRes, attendanceRes, slipsRes, incentiveRulesRes] = await Promise.all([
        adminApi.getPayrollStaff(),
        adminApi.getPayrollHolidays({ year, month }),
        adminApi.getPayrollLeaves({ year, month }),
        adminApi.getPayrollAttendance({ year, month }),
        adminApi.getPayrollSlips({ year, month }),
        adminApi.getIncentiveRules(),
      ]);
      setStaff(staffRes.data || []);
      setHolidays(holidaysRes.data || []);
      setLeaves(leavesRes.data || []);
      setAttendance(attendanceRes.data || []);
      setSlips(slipsRes.data || []);
      const activeRule = incentiveRulesRes?.data?.active_rule || null;
      const draftRule = incentiveRulesRes?.data?.draft_rule || null;
      setActiveIncentiveRule(activeRule);
      setDraftIncentiveRule(draftRule);
      if (draftRule) {
        setIncentiveRuleDraft({
          name: draftRule.name || 'Default Incentive Rule',
          basis: draftRule.basis || 'collections',
          rate_percent: Number(draftRule.rate_percent || 0),
          min_threshold: Number(draftRule.min_threshold || 0),
          max_payout: Number(draftRule.max_payout || 0),
          fixed_bonus: Number(draftRule.fixed_bonus || 0),
          effective_from: draftRule.effective_from || '',
        });
      } else if (activeRule) {
        setIncentiveRuleDraft({
          name: `${activeRule.name || 'Incentive Rule'} (Draft)`,
          basis: activeRule.basis || 'collections',
          rate_percent: Number(activeRule.rate_percent || 0),
          min_threshold: Number(activeRule.min_threshold || 0),
          max_payout: Number(activeRule.max_payout || 0),
          fixed_bonus: Number(activeRule.fixed_bonus || 0),
          effective_from: activeRule.effective_from || '',
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, [month, year]);

  const saveStaffSettings = async (member) => {
    try {
      await adminApi.updatePayrollStaffSettings(member.id, {
        base_salary: Number(member.base_salary || 0),
        weekly_off_day: Number(member.weekly_off_day ?? 6),
      });
      toast.success('Salary settings updated');
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update salary settings');
    }
  };

  const createHoliday = async () => {
    try {
      await adminApi.createPayrollHoliday(holidayForm);
      toast.success('Holiday added');
      setHolidayForm({ name: '', holiday_date: '', holiday_type: 'company', is_optional: false });
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const deleteHoliday = async (id) => {
    try {
      await adminApi.deletePayrollHoliday(id);
      toast.success('Holiday deleted');
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const createLeave = async () => {
    try {
      await adminApi.createPayrollLeave(leaveForm);
      toast.success('Leave request created');
      setLeaveForm({ staff_id: '', leave_type: 'casual', start_date: '', end_date: '', reason: '' });
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create leave request');
    }
  };

  const decideLeave = async (id, action) => {
    try {
      if (action === 'approve') {
        await adminApi.approvePayrollLeave(id, { note: '' });
      } else {
        await adminApi.rejectPayrollLeave(id, { note: '' });
      }
      toast.success(`Leave ${action}d`);
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Failed to ${action} leave`);
    }
  };

  const markAttendance = async () => {
    try {
      await adminApi.markPayrollAttendance({
        ...attendanceForm,
        leave_type: attendanceForm.status === 'leave' ? attendanceForm.leave_type : null,
      });
      toast.success('Attendance marked');
      setAttendanceForm((prev) => ({ ...prev, note: '' }));
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  const generateSlips = async () => {
    try {
      const adjustmentRows = Object.entries(adjustments).map(([staff_id, value]) => ({
        staff_id,
        incentive: Number(value?.incentive || 0),
        deductions: Number(value?.deductions || 0),
      }));
      await adminApi.generatePayrollSlips({ month, year, adjustments: adjustmentRows });
      toast.success('Salary slips generated');
      fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to generate salary slips');
    }
  };

  const updateStaffLocal = (id, patch) => {
    setStaff((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const setAdjustment = (staffId, field, value) => {
    setAdjustments((prev) => ({
      ...prev,
      [staffId]: {
        ...prev[staffId],
        [field]: value,
      },
    }));
  };

  const saveIncentiveDraft = async () => {
    setIncentiveLoading(true);
    try {
      const payload = {
        ...incentiveRuleDraft,
        rate_percent: Number(incentiveRuleDraft.rate_percent || 0),
        min_threshold: Number(incentiveRuleDraft.min_threshold || 0),
        max_payout: Number(incentiveRuleDraft.max_payout || 0),
        fixed_bonus: Number(incentiveRuleDraft.fixed_bonus || 0),
      };
      const response = await adminApi.saveIncentiveRuleDraft(payload);
      setDraftIncentiveRule(response?.data?.draft_rule || null);
      toast.success('Incentive rule draft saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save draft rule');
    } finally {
      setIncentiveLoading(false);
    }
  };

  const runIncentiveSimulation = async () => {
    setIncentiveLoading(true);
    try {
      const payload = {
        month,
        year,
        rule: {
          ...incentiveRuleDraft,
          rate_percent: Number(incentiveRuleDraft.rate_percent || 0),
          min_threshold: Number(incentiveRuleDraft.min_threshold || 0),
          max_payout: Number(incentiveRuleDraft.max_payout || 0),
          fixed_bonus: Number(incentiveRuleDraft.fixed_bonus || 0),
        },
      };
      const response = await adminApi.simulateIncentiveRule(payload);
      setIncentiveSimulation(response?.data || null);
      toast.success('Simulation generated');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to simulate rule');
    } finally {
      setIncentiveLoading(false);
    }
  };

  const publishIncentiveDraft = async () => {
    setIncentiveLoading(true);
    try {
      const response = await adminApi.publishIncentiveRule({ draft_id: draftIncentiveRule?.id });
      setActiveIncentiveRule(response?.data?.active_rule || null);
      setDraftIncentiveRule(null);
      toast.success('Incentive rule published');
      await fetchPayrollData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to publish rule');
    } finally {
      setIncentiveLoading(false);
    }
  };

  const downloadExcel = async (dataset) => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel(dataset, { year, month });
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Payroll Management</h1>
          <p className="text-[#A8A8A8] mt-1">Attendance, leaves, holidays and salary slips</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadExcel('payroll-leaves')}>
            <Download className="w-4 h-4 mr-2" /> Leaves Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('payroll-holidays')}>
            <Download className="w-4 h-4 mr-2" /> Holidays Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('payroll-slips')}>
            <Download className="w-4 h-4 mr-2" /> Slips Excel
          </Button>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-[140px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((m, idx) => (
                <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[120px] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="salary" className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full max-w-5xl">
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="rules">Incentive Rules</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="leaves">Leaves</TabsTrigger>
          <TabsTrigger value="holidays">Holidays</TabsTrigger>
          <TabsTrigger value="slips">Salary Slips</TabsTrigger>
        </TabsList>

        <TabsContent value="salary" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Staff Salary Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {staff.map((member) => (
                <div key={member.id} className="grid grid-cols-1 md:grid-cols-6 gap-3 border rounded-lg p-3">
                  <div className="md:col-span-2">
                    <p className="font-semibold text-[#2D2420]">{member.name}</p>
                    <p className="text-sm text-[#777]">{member.phone}</p>
                  </div>
                  <div>
                    <Label>Base Salary</Label>
                    <Input
                      type="number"
                      value={member.base_salary ?? 0}
                      onChange={(e) => updateStaffLocal(member.id, { base_salary: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Weekly Off</Label>
                    <Select
                      value={String(member.weekly_off_day ?? 6)}
                      onValueChange={(v) => updateStaffLocal(member.id, { weekly_off_day: Number(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEK_DAYS.map((d) => (
                          <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Incentive</Label>
                    <Input
                      type="number"
                      value={adjustments[member.id]?.incentive ?? ''}
                      onChange={(e) => setAdjustment(member.id, 'incentive', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Deductions</Label>
                    <Input
                      type="number"
                      value={adjustments[member.id]?.deductions ?? ''}
                      onChange={(e) => setAdjustment(member.id, 'deductions', e.target.value)}
                    />
                    <Button className="mt-2 w-full" variant="outline" onClick={() => saveStaffSettings(member)}>
                      Save Settings
                    </Button>
                  </div>
                </div>
              ))}
              <Button onClick={generateSlips} className="bg-[#C97A5E] hover:bg-[#B06850] text-white" disabled={loading}>
                {loading ? 'Please wait...' : `Generate ${monthNames[month - 1]} ${year} Salary Slips`}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Incentive / Commission Rules</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <Label>Rule Name</Label>
                  <Input
                    value={incentiveRuleDraft.name}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Basis</Label>
                  <Select
                    value={incentiveRuleDraft.basis}
                    onValueChange={(v) => setIncentiveRuleDraft((prev) => ({ ...prev, basis: v }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collections">Collections</SelectItem>
                      <SelectItem value="delivered_orders">Delivered Orders</SelectItem>
                      <SelectItem value="both">Delivered + Collections</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rate %</Label>
                  <Input
                    type="number"
                    value={incentiveRuleDraft.rate_percent}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, rate_percent: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Min Threshold</Label>
                  <Input
                    type="number"
                    value={incentiveRuleDraft.min_threshold}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, min_threshold: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Max Payout (0 = unlimited)</Label>
                  <Input
                    type="number"
                    value={incentiveRuleDraft.max_payout}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, max_payout: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Fixed Bonus</Label>
                  <Input
                    type="number"
                    value={incentiveRuleDraft.fixed_bonus}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, fixed_bonus: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Effective From</Label>
                  <Input
                    type="date"
                    value={incentiveRuleDraft.effective_from || ''}
                    onChange={(e) => setIncentiveRuleDraft((prev) => ({ ...prev, effective_from: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={saveIncentiveDraft} disabled={incentiveLoading}>Save Draft</Button>
                <Button onClick={runIncentiveSimulation} disabled={incentiveLoading}>Simulate ({monthNames[month - 1]} {year})</Button>
                <Button className="bg-[#C97A5E] hover:bg-[#B06850] text-white" onClick={publishIncentiveDraft} disabled={incentiveLoading || !draftIncentiveRule}>
                  Publish Draft
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-md bg-[#FAFAF9] border border-[#E5E5E5]">
                  <p className="font-semibold text-[#2D2420] mb-1">Active Rule</p>
                  {activeIncentiveRule ? (
                    <p className="text-[#5C4033]">{activeIncentiveRule.name} • {activeIncentiveRule.basis} • {activeIncentiveRule.rate_percent}%</p>
                  ) : (
                    <p className="text-[#A8A8A8]">No active rule</p>
                  )}
                </div>
                <div className="p-3 rounded-md bg-[#FAFAF9] border border-[#E5E5E5]">
                  <p className="font-semibold text-[#2D2420] mb-1">Draft Rule</p>
                  {draftIncentiveRule ? (
                    <p className="text-[#5C4033]">{draftIncentiveRule.name} • v{draftIncentiveRule.version}</p>
                  ) : (
                    <p className="text-[#A8A8A8]">No draft rule</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Simulation Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {incentiveSimulation ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div className="p-2 rounded bg-[#FAFAF9]">Staff: <strong>{incentiveSimulation?.totals?.staff_count || 0}</strong></div>
                    <div className="p-2 rounded bg-[#FAFAF9]">Delivered: <strong>₹{Number(incentiveSimulation?.totals?.total_delivered_amount || 0).toLocaleString('en-IN')}</strong></div>
                    <div className="p-2 rounded bg-[#FAFAF9]">Collections: <strong>₹{Number(incentiveSimulation?.totals?.total_collections_amount || 0).toLocaleString('en-IN')}</strong></div>
                    <div className="p-2 rounded bg-[#FAFAF9]">Payout: <strong>₹{Number(incentiveSimulation?.totals?.total_payout || 0).toLocaleString('en-IN')}</strong></div>
                  </div>
                  <div className="space-y-2">
                    {(incentiveSimulation?.items || []).slice(0, 20).map((row) => (
                      <div key={row.staff_id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div>
                          <p className="font-medium text-[#2D2420]">{row.staff_name}</p>
                          <p className="text-sm text-[#777]">Metric: ₹{Number(row.metric_value || 0).toLocaleString('en-IN')} • Rate: {row.rate_percent}% • Bonus: ₹{Number(row.fixed_bonus || 0).toLocaleString('en-IN')}</p>
                        </div>
                        <Badge>Projected Incentive: ₹{Number(row.payout || 0).toLocaleString('en-IN')}</Badge>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#777]">Run simulation to preview payouts before publish.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attendance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Mark Attendance</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <Label>Staff</Label>
                <Select value={attendanceForm.staff_id} onValueChange={(v) => setAttendanceForm((p) => ({ ...p, staff_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={attendanceForm.attendance_date} onChange={(e) => setAttendanceForm((p) => ({ ...p, attendance_date: e.target.value }))} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={attendanceForm.status} onValueChange={(v) => setAttendanceForm((p) => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ATTENDANCE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Leave Type</Label>
                <Select value={attendanceForm.leave_type} onValueChange={(v) => setAttendanceForm((p) => ({ ...p, leave_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Input value={attendanceForm.note} onChange={(e) => setAttendanceForm((p) => ({ ...p, note: e.target.value }))} />
              </div>
              <div className="md:col-span-6">
                <Button onClick={markAttendance}>Mark Attendance</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Attendance Register ({monthNames[month - 1]} {year})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {attendance.map((item) => (
                <div key={item.id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div>
                    <p className="font-medium text-[#2D2420]">{item.staff_name} • {item.attendance_date}</p>
                    <p className="text-sm text-[#777]">Login: {formatDateTime(item.check_in_at)} • Logout: {formatDateTime(item.check_out_at)}</p>
                    <p className="text-sm text-[#777]">{item.note || 'No note'} {item.latitude ? `• ${item.latitude.toFixed(5)}, ${item.longitude?.toFixed(5)}` : ''}</p>
                  </div>
                  <Badge>{item.status}{item.leave_type ? ` (${item.leave_type})` : ''}</Badge>
                </div>
              ))}
              {!attendance.length && <p className="text-sm text-[#777]">No attendance records for selected month.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create Leave (On Behalf of Staff)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <Label>Staff</Label>
                <Select value={leaveForm.staff_id} onValueChange={(v) => setLeaveForm((p) => ({ ...p, staff_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select staff" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm((p) => ({ ...p, leave_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>From</Label>
                <Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm((p) => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm((p) => ({ ...p, end_date: e.target.value }))} />
              </div>
              <div>
                <Label>Reason</Label>
                <Input value={leaveForm.reason} onChange={(e) => setLeaveForm((p) => ({ ...p, reason: e.target.value }))} />
              </div>
              <div className="md:col-span-6">
                <Button onClick={createLeave}>Create Leave Request</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Leave Requests</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {leaves.map((leave) => (
                <div key={leave.id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#2D2420]">{leave.staff_name} • {leave.leave_type}</p>
                    <p className="text-sm text-[#777]">{leave.start_date} to {leave.end_date} ({leave.days} day(s))</p>
                    <p className="text-sm text-[#777]">{leave.reason || 'No reason'}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Badge variant={leave.status === 'approved' ? 'default' : leave.status === 'rejected' ? 'destructive' : 'secondary'}>
                      {leave.status}
                    </Badge>
                    {leave.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => decideLeave(leave.id, 'approve')}>Approve</Button>
                        <Button size="sm" variant="destructive" onClick={() => decideLeave(leave.id, 'reject')}>Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {!leaves.length && <p className="text-sm text-[#777]">No leave requests for selected month.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="holidays" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Holiday Calendar</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={holidayForm.name} onChange={(e) => setHolidayForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={holidayForm.holiday_date} onChange={(e) => setHolidayForm((p) => ({ ...p, holiday_date: e.target.value }))} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={holidayForm.holiday_type} onValueChange={(v) => setHolidayForm((p) => ({ ...p, holiday_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="national">National</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Optional</Label>
                <Select value={holidayForm.is_optional ? 'yes' : 'no'} onValueChange={(v) => setHolidayForm((p) => ({ ...p, is_optional: v === 'yes' }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={createHoliday} className="w-full">Add Holiday</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Holidays ({monthNames[month - 1]} {year})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {holidays.map((h) => (
                <div key={h.id} className="border rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#2D2420]">{h.name}</p>
                    <p className="text-sm text-[#777]">{h.holiday_date} • {h.holiday_type} {h.is_optional ? '• optional' : ''}</p>
                  </div>
                  <Button variant="destructive" size="sm" onClick={() => deleteHoliday(h.id)}>Delete</Button>
                </div>
              ))}
              {!holidays.length && <p className="text-sm text-[#777]">No holidays for selected month.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="slips" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Salary Slips ({monthNames[month - 1]} {year})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {slips.map((s) => (
                <div key={s.id} className="border rounded-lg p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <p className="font-semibold text-[#2D2420]">{s.staff_name}</p>
                      <p className="text-sm text-[#777]">Base Salary: ₹{Number(s.base_salary).toLocaleString('en-IN')}</p>
                    </div>
                    <Badge>Net Payable: ₹{Number(s.net_payable).toLocaleString('en-IN')}</Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-sm">
                    <div>Attendance Adj: ₹{Number(s.attendance_adjustment).toLocaleString('en-IN')}</div>
                    <div>Incentive: ₹{Number(s.incentive).toLocaleString('en-IN')}</div>
                    <div>Deductions: ₹{Number(s.deductions).toLocaleString('en-IN')}</div>
                    <div>Payable Days: {s.payable_days}/{s.working_days}</div>
                    <div>Unpaid Days: {s.unpaid_days}</div>
                  </div>
                </div>
              ))}
              {!slips.length && <p className="text-sm text-[#777]">No salary slips generated for selected month.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
